import React from 'react';
import {
    IEntity, ICustomField, IEntityWithTimerange, ICustomFieldDfn, ILookupData, IReportingEntity, IUser, ICommitment,
    IKeyWorkArea, IBenefit, IDependency, IMilestone, IPartnerOrganisationRisk, IWorkStream, IRisk,
    IPartnerOrganisationRiskMitigationAction, IRiskMitigationAction, ICustomReportingEntity
} from '../types';
import { ISelectableOption } from '@fluentui/react/lib/SelectableOption';
import { IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { ICrMultiDropdownWithTextOption, ICrMultiDropdownWithTextValue } from '../components/cr/CrMultiDropdownWithText';
import { RiskRegister } from '../refData/RiskRegister';
import { IDropdownOption } from '@fluentui/react';
import { DateService } from './DateService';

export class LookupService {

    public static entitiesToSelectableOptions = <T extends IEntity>(
        entities: T[],
        formatters?: { optionKey?: (entity: T) => string, optionText?: (entity: T) => string }
    ): ISelectableOption[] => {
        return entities ? formatters ?
            entities.map(e => ({ key: formatters.optionKey?.(e) || e.ID, text: formatters.optionText?.(e) || e.Title }))
            :
            entities.map(e => ({ key: e.ID, text: e.Title }))
            :
            [];
    }

    public static entitiesToSelectableOptionsCustom = <
        T extends { ID: number | string; Title: string }
    >(
        entities: T[],
        formatters?: {
            optionKey?: (entity: T) => string | number;
            optionText?: (entity: T) => string;
        }
    ): ISelectableOption[] => {
        return entities
            ? formatters
                ? entities.map(e => ({
                    key: formatters.optionKey?.(e) ?? e.ID,
                    text: formatters.optionText?.(e) ?? e.Title,
                }))
                : entities.map(e => ({
                    key: e.ID,
                    text: e.Title,
                }))
            : [];
    };




    public static entitiesToSelectableOptions2 = <T extends IEntity>(
        entities: T[],
        formatters?: { optionKey?: (entity: T) => string, optionText?: (entity: T) => string }
    ): ISelectableOption[] => {
        return entities.map(e => {
            const option: ISelectableOption = {
                key: formatters?.optionKey?.(e) || e.ID,
                text: formatters?.optionText?.(e) || e.Title || '',
                // Add any other properties you need to assign
            };
            return option;
        });
    }


    public static entitiesToSelectableOptionsFilteredByDate = (filterDate: Date, entities: IEntityWithTimerange[]): ISelectableOption[] =>
        entities?.filter(en => (!DateService.removeTimezoneOffset(en.StartUpdatePeriod) || filterDate >= DateService.removeTimezoneOffset(en.StartUpdatePeriod)) && (!DateService.removeTimezoneOffset(en.EndUpdatePeriod) || filterDate <= DateService.removeTimezoneOffset(en.EndUpdatePeriod)))
            .map((li): ISelectableOption => ({
                key: li.ID,
                text: li.Title ?? '', // Use the nullish coalescing operator to ensure a non-null string
            })) || [];


    private static renderLabel(option?: IChoiceGroupOption, description?: string) {
        if (option) {
            return (
                <TooltipHost content={description}>
                    <span style={{ paddingLeft: '26px' }}>{option.text}</span>
                </TooltipHost>
            );
        }
        return null;
    }


    public static entitiesToChoiceGroupOptionsFilteredByDate = (filterDate: Date, entities: IEntityWithTimerange[]): IChoiceGroupOption[] => {
        if (filterDate instanceof Date == false)
            filterDate = DateService.removeTimezoneOffset(filterDate);
        const retEntities = entities
            ?.filter(e => (!DateService.removeTimezoneOffset(e.StartUpdatePeriod) || filterDate >= DateService.removeTimezoneOffset(e.StartUpdatePeriod)) && (!DateService.removeTimezoneOffset(e.EndUpdatePeriod) || filterDate <= DateService.removeTimezoneOffset(e.EndUpdatePeriod)))
            .map((e): IChoiceGroupOption => {
                return {
                    key: e.ID?.toString(),
                    text: e.Title || '',
                    onRenderLabel: (option?: IChoiceGroupOption) => this.renderLabel(option, e.Description ?? '') // Provide a default value
                };
            }) || [];
        return retEntities;
    }

    public static entitiesToChoiceGroupOptions = (entities: IEntity[]): IChoiceGroupOption[] =>
        entities?.map((e): IChoiceGroupOption =>
        ({
            key: e.ID?.toString(),
            text: e.Title || '',
            onRenderLabel: option => LookupService.renderLabel(option, e.Description ?? '')
        })
        ) || []

    public static customFieldsToDropdownWithText = (customFields: ICustomField[]): ICrMultiDropdownWithTextValue[] =>
        customFields?.filter(a => a.CustomFieldDefnID !== null) // Filter out items with null CustomFieldDefnID
            .map(a => ({ Key: a.CustomFieldDefnID!, Text: a.CustomFieldValue })) || []


    public static customFieldDfnsToMultiDropdownWithTextOptions = (customFieldDfns: ICustomFieldDfn[]): ICrMultiDropdownWithTextOption[] =>
        customFieldDfns?.map(a => ({ key: a.ID, text: a.Title, textRequired: false })) || []

    public static getLookupName = (lookupData: IEntity[], optionId: number | string, noMatchText = 'To be completed'): string =>
        lookupData?.find(ld => ld.ID === Number(optionId))?.Title || noMatchText

    public static userIsInDirectorate = (lookupData: ILookupData, userId: number, directorateId: number): boolean =>
        lookupData?.UserDirectorates?.filter(ud => ud.DirectorateID === directorateId).some(ud => ud.UserID === userId) || false

    public static userIsInGroup = (lookupData: ILookupData, userId: number, groupId: number): boolean =>
        lookupData?.UserGroups?.filter(ug => ug.GroupID === groupId).some(ug => ug.UserID === userId) || false

    public static userIsInGroupDirectorates = (lookupData: ILookupData, userId: number, groupId: number): boolean => {
        const groupDirectorates = lookupData.Directorates
            .filter(d => d.GroupID === groupId)
            .map(d => d.ID)
            .filter(id => id !== null) // Filter out null values

        return lookupData?.UserDirectorates
            ?.filter(ud => ud.DirectorateID !== null && groupDirectorates.includes(ud.DirectorateID))
            .some(ud => ud.UserID === userId) || false;
    }



    public static directorateUsers = (ld: ILookupData, directorateId: number, entity: ICommitment | IKeyWorkArea | IMilestone | ICustomReportingEntity): IUser[] => {
        if (ld?.Users?.All && directorateId && entity) {
            return ld.Users.All.filter(u => LookupService.userIsInDirectorate(ld, u.ID, directorateId)
                || LookupService.userIsContributor(entity, u.ID)
                || u.ID === entity.LeadUserID
            );
        }
        return [];
    }

    public static userIsInProject = (lookupData: ILookupData, userId: number, projectId: number): boolean =>
        lookupData?.UserProjects?.filter(up => up.ProjectID === projectId).some(up => up.UserID === userId) || false

    public static projectUsers = (ld: ILookupData, projectId: number, entity: IBenefit | IDependency | IMilestone | IWorkStream | ICustomReportingEntity): IUser[] => {
        if (ld?.Users?.All && projectId && entity) {
            return ld.Users.All.filter(u => LookupService.userIsInProject(ld, u.ID, projectId)
                || LookupService.userIsContributor(entity, u.ID)
                || u.ID === entity.LeadUserID
            );
        }
        return [];
    }

    public static userIsInPartnerOrganisation = (lookupData: ILookupData, userId: number, partnerOrganisationId: number): boolean =>
        lookupData?.UserPartnerOrganisations?.filter(upo => upo.PartnerOrganisationID === partnerOrganisationId).some(upo => upo.UserID === userId) || false

    public static partnerOrganisationUsers = (ld: ILookupData, partnerOrganisationId: number, entity: IMilestone | ICustomReportingEntity): IUser[] => {
        if (ld?.Users?.All && partnerOrganisationId) {
            return ld.Users.All.filter(u => LookupService.userIsInPartnerOrganisation(ld, u.ID, partnerOrganisationId)
                || LookupService.userIsContributor(entity, u.ID)
                || u.ID === entity.LeadUserID
            );
        }
        return [];
    }

    public static userIsContributor = (entity: IReportingEntity, userId: number): boolean =>
        entity?.Contributors?.some(c => c.ContributorUserID === userId) || false

    public static riskUsers = (ld: ILookupData, risk: IRisk): IUser[] => {
        if (ld?.Users?.All && risk) {
            return ld?.Users?.All;
            // if (risk.IsProjectRisk && risk.ProjectID) {
            //     if (risk.RiskRegisterID === RiskRegister.Group) {
            //         const groupId = ld.Directorates.find(d => d.ID === risk.DirectorateID)?.GroupID;
            //         return ld.Users.All.filter(u =>
            //             LookupService.userIsInProject(ld, u.ID, risk.ProjectID ?? 0)
            //             || LookupService.userIsInGroupDirectorates(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsContributor(risk, u.ID)
            //             || u.ID === risk.RiskOwnerUserID
            //             || u.ID === risk.ReportApproverUserID
            //         );
            //     }

            //     return ld.Users.All.filter(u =>
            //         LookupService.userIsInProject(ld, u.ID, risk.ProjectID ?? 0)
            //         || LookupService.userIsContributor(risk, u.ID)
            //         || u.ID === risk.RiskOwnerUserID
            //         || u.ID === risk.ReportApproverUserID
            //     );
            // } else if (risk.DirectorateID) {
            //     const groupId = ld.Directorates.find(d => d.ID === risk.DirectorateID)?.GroupID;
            //     if (risk.RiskRegisterID === RiskRegister.Group) {
            //         return ld.Users.All.filter(u =>
            //             LookupService.userIsInGroup(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsInGroupDirectorates(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsContributor(risk, u.ID)
            //             || u.ID === risk.RiskOwnerUserID
            //             || u.ID === risk.ReportApproverUserID
            //         );
            //     }

            //     return ld.Users.All.filter(u =>
            //         LookupService.userIsInDirectorate(ld, u.ID, risk.DirectorateID ?? 0)
            //         || LookupService.userIsInGroup(ld, u.ID, groupId ?? 0)
            //         || LookupService.userIsContributor(risk, u.ID)
            //         || u.ID === risk.RiskOwnerUserID
            //         || u.ID === risk.ReportApproverUserID
            //     );
            // }
        }
        return [];
    }

    public static riskMitigationActionUsers = (ld: ILookupData, action: IRiskMitigationAction): IUser[] => {
        if (ld?.Users?.All && action.RiskID) {
            return ld?.Users?.All;
            // const corpRisk = ld.CorporateRisks.find(r => r.ID === action.RiskID);
            // const finRisk = ld.FinancialRisks.find(r => r.ID === action.RiskID);
            // if (finRisk) {
            //     return ld.Users.All;
            // } else if (corpRisk?.IsProjectRisk && corpRisk?.ProjectID) {
            //     if (corpRisk.RiskRegisterID === RiskRegister.Group) {
            //         const groupId = ld.Directorates.find(d => d.ID === corpRisk.DirectorateID)?.GroupID;
            //         return ld.Users.All.filter(u =>
            //             LookupService.userIsInProject(ld, u.ID, corpRisk.ProjectID ?? 0)
            //             || LookupService.userIsInGroupDirectorates(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsContributor(action, u.ID)
            //             || u.ID === action.OwnerUserID
            //         );
            //     }
            //     return ld.Users.All.filter(u =>
            //         LookupService.userIsInProject(ld, u.ID, corpRisk.ProjectID ?? 0)
            //         || LookupService.userIsContributor(action, u.ID)
            //         || u.ID === action.OwnerUserID
            //     );
            // } else if (corpRisk?.DirectorateID) {
            //     const groupId = ld.Directorates.find(d => d.ID === corpRisk.DirectorateID)?.GroupID;
            //     if (corpRisk.RiskRegisterID === RiskRegister.Group) {
            //         return ld.Users.All.filter(u =>
            //             LookupService.userIsInDirectorate(ld, u.ID, corpRisk.DirectorateID ?? 0)
            //             || LookupService.userIsInGroup(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsInGroupDirectorates(ld, u.ID, groupId ?? 0)
            //             || LookupService.userIsContributor(action, u.ID)
            //             || u.ID === action.OwnerUserID
            //         );
            //     }
            //     return ld.Users.All.filter(u =>
            //         LookupService.userIsInDirectorate(ld, u.ID, corpRisk.DirectorateID ?? 0)
            //         || LookupService.userIsInGroup(ld, u.ID, groupId ?? 0)
            //         || LookupService.userIsContributor(action, u.ID)
            //         || u.ID === action.OwnerUserID
            //     );
            // }
        }
        return [];
    }

    public static partnerOrganisationRiskUsers = (ld: ILookupData, risk: IPartnerOrganisationRisk): IUser[] => {
        if (ld?.Users?.All && risk?.PartnerOrganisationID) {
            return ld.Users.All.filter(u => LookupService.userIsInPartnerOrganisation(ld, u.ID, risk.PartnerOrganisationID ?? 0)
                || LookupService.userIsContributor(risk, u.ID)
                || u.ID === risk.BeisRiskOwnerUserID
                || u.ID === risk.RiskOwnerUserID
            );
        }
        return [];
    }

    public static partnerOrganisationRiskMitigationActionUsers = (ld: ILookupData, risk: IPartnerOrganisationRisk, action: IPartnerOrganisationRiskMitigationAction): IUser[] => {
        if (ld?.Users?.All && risk?.PartnerOrganisationID) {
            return ld.Users.All.filter(u => LookupService.userIsInPartnerOrganisation(ld, u.ID, risk.PartnerOrganisationID ?? 0)
                || LookupService.userIsContributor(action, u.ID)
                || u.ID === action.OwnerUserID
            );
        }
        return [];
    }

    public static arrayToDropdownOptions = (arr: string[]): IDropdownOption[] => arr?.map(e => ({ key: e, text: e }));
}
